// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { AssetLocationFilterMenu } from '@/asset/components/asset-location-filter-menu';
import { ActionMenuItem } from '@/components/action-menu-item';
import { EducationalTooltip } from '@/components/educational-tooltip';
import {
  FilterOption,
  FilterToggleGroup,
} from '@/components/filter-toggle-group';
import { SupportErrorBoundary } from '@/components/support-error-boundary';
import { TableControlContainer } from '@/components/table-control-container';
import {
  AddRounded,
  ArrowDropDownRounded,
  ClearRounded,
  InfoOutlined,
  MultipleStopOutlined,
} from '@/constants/hardfin-standard-icons';
import type { LocationCompanyType } from '@/constants/location-company-type';
import { SEARCH_TERM_MAXIMUM_CHARACTERS } from '@/constants/search-term';
import { invert } from '@/constants/sort';
import { useDisclosure } from '@/hooks/use-disclosure';
import {
  type ShipmentFilter,
  getShipments,
} from '@/shipment/api/get-shipments';
import type {
  Shipment,
  ShipmentStatus,
  ShipmentType,
} from '@/shipment/api/types';
import { InboundShipmentDialog } from '@/shipment/components/inbound-shipment-dialog';
import { NeedsAttentionFilterChip } from '@/shipment/components/needs-attention-filter-chip';
import { NeedsAttentionFilterChipBase } from '@/shipment/components/needs-attention-filter-chip-base';
import { OutboundShipDialog } from '@/shipment/components/outbound-ship-dialog';
import { OutboundShipmentDialog } from '@/shipment/components/outbound-shipment-dialog';
import { QuarantinedFilterChip } from '@/shipment/components/quarantined-filter-chip';
import { QuarantinedFilterChipBase } from '@/shipment/components/quarantined-filter-chip-base';
import { ShipmentDataImportDialog } from '@/shipment/components/shipment-data-import-dialog';
import { ShipmentTable } from '@/shipment/components/shipment-table';
import { ShipmentTableSkeleton } from '@/shipment/components/shipment-table-skeleton';
import { ShipmentStatusFilter } from '@/shipment/constants/status-filter-options';
import {
  ShipmentSearchListing,
  shipmentsQueryKey,
} from '@/shipment/hooks/use-shipments';
import {
  Box,
  Button,
  ButtonGroup,
  IconButton,
  InputAdornment,
  Link,
  Menu,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { debounce } from 'lodash-es';
import {
  ChangeEventHandler,
  Suspense,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

/**
 * Search parameters for shipment route navigation.
 * Includes 'all' as a valid status for filtering. The shape is permissive
 * so callers can spread `searchParameters` (which is keyed by the listing
 * schema) directly into navigation calls.
 */
type ShipmentRouteSearch = Omit<Partial<ShipmentSearchListing>, 'status'> &
  Record<string, unknown> & {
    type?: ShipmentType;
    status?: ShipmentStatus | 'all';
    page?: number;
    query?: string;
    startOn?: string;
    endOn?: string;
  };

/**
 * Navigation options for the shipment listing.
 */
interface NavigateOptions {
  to?: string;
  search?: ShipmentRouteSearch;
}

interface ShipmentListingProps {
  currentStatus: ShipmentStatus | 'all';
  shipmentType: ShipmentType;
  statusOptions: FilterOption<ShipmentStatusFilter>[];
  searchParameters: ShipmentSearchListing;
  navigateFn: (options: NavigateOptions) => void;
}

export function ShipmentListing(props: ShipmentListingProps) {
  const searchContainer = useRef<HTMLInputElement>(null);
  const actionGroupRef = useRef<HTMLDivElement>(null);
  const shipmentDialog = useDisclosure();
  const uploadDialog = useDisclosure();
  const actionMenu = useDisclosure();
  const shipDialog = useDisclosure();
  const infoTooltip = useDisclosure();
  const infoButtonRef = useRef<HTMLButtonElement>(null);
  const [editingShipShipment, setEditingShipShipment] = useState<
    Shipment | undefined
  >(undefined);
  const searchParametersRef = useRef(props.searchParameters);
  const navigateFnRef = useRef(props.navigateFn);
  const shipmentTypeRef = useRef(props.shipmentType);
  const currentStatusRef = useRef(props.currentStatus);
  searchParametersRef.current = props.searchParameters;
  navigateFnRef.current = props.navigateFn;
  shipmentTypeRef.current = props.shipmentType;
  currentStatusRef.current = props.currentStatus;

  /**
   * Debounced search query handler.
   */
  const updateQuery = useMemo(() => {
    return debounce((query: string) => {
      navigateFnRef.current({
        search: {
          ...searchParametersRef.current,
          type: shipmentTypeRef.current,
          status: currentStatusRef.current,
          page: undefined,
          query: query.length === 0 ? undefined : query,
        },
      });
    }, 300);
  }, []);

  // Cancel on unmount
  useEffect(() => {
    return () => {
      updateQuery.cancel();
    };
  }, [updateQuery]);

  /**
   * Handle status toggle change.
   */
  const handleStatusChange = (newStatus: ShipmentStatus | 'all') => {
    updateQuery.cancel();
    props.navigateFn({
      search: {
        ...props.searchParameters,
        type: props.shipmentType,
        status: newStatus,
        page: undefined,
      },
    });
  };

  const onPageChange = (pageNumber: number) => {
    updateQuery.cancel();
    props.navigateFn({
      search: {
        ...props.searchParameters,
        type: props.shipmentType,
        status: props.currentStatus,
        page: pageNumber,
      },
    });
  };

  /**
   * Toggle the "needs attention" filter, which restricts to shipments with
   * active attention. Composes with the status and location filters.
   */
  const handleNeedsAttentionToggle = () => {
    updateQuery.cancel();
    props.navigateFn({
      search: {
        ...props.searchParameters,
        type: props.shipmentType,
        status: props.currentStatus,
        page: undefined,
        needsAttention: props.searchParameters.needsAttention
          ? undefined
          : true,
      },
    });
  };

  /**
   * Toggle the "quarantined" filter, which restricts to shipments holding a
   * quarantined serial. Composes with the status and location filters.
   */
  const handleQuarantinedToggle = () => {
    updateQuery.cancel();
    props.navigateFn({
      search: {
        ...props.searchParameters,
        type: props.shipmentType,
        status: props.currentStatus,
        page: undefined,
        quarantined: props.searchParameters.quarantined ? undefined : true,
      },
    });
  };

  const handleSort = (property: NonNullable<ShipmentSearchListing['sort']>) => {
    updateQuery.cancel();
    const isActiveSort = props.searchParameters.sort === property;
    const nextDirection = isActiveSort
      ? invert(props.searchParameters.direction ?? 'desc')
      : 'desc';

    props.navigateFn({
      search: {
        ...props.searchParameters,
        type: props.shipmentType,
        status: props.currentStatus,
        page: undefined,
        sort: property,
        direction: nextDirection,
      },
    });
  };

  /**
   * Handle search input changes.
   */
  const handleChangeQuery: ChangeEventHandler<HTMLInputElement> = (event) => {
    updateQuery(event.target.value);
  };

  /**
   * Handle a change to the "from" location filter selection.
   */
  const handleChangeFromLocations = (
    locationIds: string[],
    customerIds: string[],
    companyTypes: LocationCompanyType[]
  ) => {
    props.navigateFn({
      search: {
        ...props.searchParameters,
        type: props.shipmentType,
        status: props.currentStatus,
        page: undefined,
        fromLocations: locationIds.length > 0 ? locationIds : undefined,
        fromCustomerLocations: customerIds.length > 0 ? customerIds : undefined,
        fromLocationCompanyTypes:
          companyTypes.length > 0 ? companyTypes : undefined,
      },
    });
  };

  /**
   * Handle a change to the "to" location filter selection.
   */
  const handleChangeToLocations = (
    locationIds: string[],
    customerIds: string[],
    companyTypes: LocationCompanyType[]
  ) => {
    props.navigateFn({
      search: {
        ...props.searchParameters,
        type: props.shipmentType,
        status: props.currentStatus,
        page: undefined,
        toLocations: locationIds.length > 0 ? locationIds : undefined,
        toCustomerLocations: customerIds.length > 0 ? customerIds : undefined,
        toLocationCompanyTypes:
          companyTypes.length > 0 ? companyTypes : undefined,
      },
    });
  };

  const fromLocationIds = props.searchParameters.fromLocations ?? [];
  const fromCustomerIds = props.searchParameters.fromCustomerLocations ?? [];
  const fromCompanyTypes =
    props.searchParameters.fromLocationCompanyTypes ?? [];
  const toLocationIds = props.searchParameters.toLocations ?? [];
  const toCustomerIds = props.searchParameters.toCustomerLocations ?? [];
  const toCompanyTypes = props.searchParameters.toLocationCompanyTypes ?? [];
  const shipmentFilter = useMemo<ShipmentFilter>(
    () => ({
      status: props.currentStatus === 'all' ? undefined : props.currentStatus,
      shipmentType: props.shipmentType,
      query: props.searchParameters.query,
      pageNumber: props.searchParameters.page,
      pageSize: 20,
      fromLocationIds: props.searchParameters.fromLocations?.length
        ? props.searchParameters.fromLocations
        : undefined,
      fromCustomerIds: props.searchParameters.fromCustomerLocations?.length
        ? props.searchParameters.fromCustomerLocations
        : undefined,
      fromLocationCompanyTypes: props.searchParameters.fromLocationCompanyTypes
        ?.length
        ? props.searchParameters.fromLocationCompanyTypes
        : undefined,
      toLocationIds: props.searchParameters.toLocations?.length
        ? props.searchParameters.toLocations
        : undefined,
      toCustomerIds: props.searchParameters.toCustomerLocations?.length
        ? props.searchParameters.toCustomerLocations
        : undefined,
      toLocationCompanyTypes: props.searchParameters.toLocationCompanyTypes
        ?.length
        ? props.searchParameters.toLocationCompanyTypes
        : undefined,
      needsAttention: props.searchParameters.needsAttention || undefined,
      quarantined: props.searchParameters.quarantined || undefined,
      sort: props.searchParameters.sort,
      direction: props.searchParameters.direction,
    }),
    [
      props.currentStatus,
      props.searchParameters.direction,
      props.searchParameters.fromCustomerLocations,
      props.searchParameters.fromLocationCompanyTypes,
      props.searchParameters.fromLocations,
      props.searchParameters.needsAttention,
      props.searchParameters.quarantined,
      props.searchParameters.page,
      props.searchParameters.query,
      props.searchParameters.sort,
      props.searchParameters.toCustomerLocations,
      props.searchParameters.toLocationCompanyTypes,
      props.searchParameters.toLocations,
      props.shipmentType,
    ]
  );
  const { data } = useQuery({
    queryKey: [...shipmentsQueryKey, shipmentFilter],
    queryFn: () => getShipments(shipmentFilter),
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: true,
    refetchInterval: 30_000,
  });
  const statusOptions = props.statusOptions.map((option) => ({
    ...option,
    count: data?.statusCounts[option.value],
  }));

  const handleShipShipment = (shipment: Shipment) => {
    setEditingShipShipment(shipment);
    shipDialog.open();
  };

  const handleCloseShipDialog = () => {
    setEditingShipShipment(undefined);
    shipDialog.close();
  };

  return (
    <Stack>
      {props.shipmentType === 'inbound' ? (
        <InboundShipmentDialog
          isOpen={shipmentDialog.isOpen}
          onClose={shipmentDialog.close}
        />
      ) : (
        <OutboundShipmentDialog
          isOpen={shipmentDialog.isOpen}
          onClose={shipmentDialog.close}
        />
      )}
      <OutboundShipDialog
        isOpen={shipDialog.isOpen}
        onClose={handleCloseShipDialog}
        shipment={editingShipShipment}
      />
      <ShipmentDataImportDialog
        isOpen={uploadDialog.isOpen}
        onClose={uploadDialog.close}
      />

      {/* Action bar */}
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack direction="row" alignItems="center" spacing={1}>
          <FilterToggleGroup
            value={props.currentStatus}
            options={statusOptions}
            onChange={handleStatusChange}
            ariaLabel="Shipment status filter"
          />
          {(props.shipmentType === 'inbound' ||
            props.shipmentType === 'outbound') && (
            <IconButton
              ref={infoButtonRef}
              size="small"
              onClick={infoTooltip.open}
              aria-label={
                props.shipmentType === 'inbound'
                  ? 'Learn about inbound shipments'
                  : 'Learn about outbound shipments'
              }
            >
              <InfoOutlined fontSize="small" />
            </IconButton>
          )}
        </Stack>
        <Stack direction="row" spacing={1}>
          <ButtonGroup
            size="small"
            variant="contained"
            ref={actionGroupRef}
            sx={{ alignSelf: 'end' }}
          >
            <Button
              startIcon={<AddRounded fontSize="small" />}
              onClick={shipmentDialog.open}
            >
              {props.shipmentType === 'inbound'
                ? 'Add inbound shipment'
                : 'Add outbound shipment'}
            </Button>
            <Button onClick={actionMenu.open}>
              <ArrowDropDownRounded fontSize="small" />
            </Button>
          </ButtonGroup>
          <Menu
            open={actionMenu.isOpen}
            onClose={actionMenu.close}
            onClick={actionMenu.close}
            anchorEl={actionGroupRef.current}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            MenuListProps={{ disablePadding: true }}
            slotProps={{
              paper: {
                sx: { maxWidth: '100vw', width: '25rem', marginTop: 0.5 },
              },
            }}
          >
            <ActionMenuItem
              icon={<MultipleStopOutlined color="action" fontSize="small" />}
              label="Import shipment data"
              description="Import shipment data in bulk from a CSV file"
              onClick={uploadDialog.open}
            />
          </Menu>
        </Stack>
      </Stack>

      {/* Educational tooltip explaining inbound shipments and stages */}
      {props.shipmentType === 'inbound' && (
        <EducationalTooltip
          title="Inbound shipments"
          isOpen={infoTooltip.isOpen}
          onClose={infoTooltip.close}
          anchorElement={infoButtonRef.current}
          placement="bottom-start"
        >
          <Stack spacing={2}>
            <Box>
              <Typography variant="body2">
                <strong>Inbound shipments</strong> track parts and products
                arriving at your facilities from suppliers. They support the
                inventory workflow for both parts and products.
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Shipment stages
              </Typography>
              <Typography variant="body2">
                <strong>Open:</strong> Shipment has been created but not yet
                delivered to the destination location.
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Delivered:</strong> Shipment has been delivered to the
                destination location but items have not yet been received into
                inventory.
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Received:</strong> Shipment has been received into
                inventory. Parts are added to parts inventory, and products are
                automatically registered as serialized assets.
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, mb: 0 }}>
                <strong>Cancelled:</strong> Shipment has been cancelled and will
                not be processed. No inventory changes will occur.
              </Typography>
            </Box>
            <Typography variant="body2">
              <Link
                href="https://guide.hardfin.com/source-and-build/inbound-shipments"
                target="_blank"
              >
                Read more about inbound shipments
              </Link>
            </Typography>
          </Stack>
        </EducationalTooltip>
      )}

      {/* Educational tooltip explaining outbound shipments and stages */}
      {props.shipmentType === 'outbound' && (
        <EducationalTooltip
          title="Outbound shipments"
          isOpen={infoTooltip.isOpen}
          onClose={infoTooltip.close}
          anchorElement={infoButtonRef.current}
          placement="bottom-start"
        >
          <Stack spacing={2}>
            <Box>
              <Typography variant="body2">
                <strong>Outbound shipments</strong> track assets and parts
                leaving your facilities for delivery to customers or other
                locations. They support the fulfillment workflow for delivering
                finished goods.
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Shipment stages
              </Typography>
              <Typography variant="body2">
                <strong>Open:</strong> Shipment has been created but has not yet
                left your facility. Assets and parts are still at the origin
                location.
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Shipped:</strong> Shipment has left your facility and
                items are tracked as in-transit inventory. Assets and parts are
                no longer at your facility but haven't arrived at the
                destination yet.
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Delivered:</strong> Shipment has arrived at its
                destination. Assets and parts are now at the destination
                location and no longer in-transit.
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, mb: 0 }}>
                <strong>Cancelled:</strong> Shipment has been cancelled and will
                not be processed. No inventory changes will occur.
              </Typography>
            </Box>
            <Typography variant="body2">
              <Link
                href="https://guide.hardfin.com/fulfill-and-deploy-transit/outbound-shipments"
                target="_blank"
              >
                Read more about outbound shipments
              </Link>
            </Typography>
          </Stack>
        </EducationalTooltip>
      )}

      {/* Table controls */}
      <TableControlContainer>
        <Stack
          direction="row"
          alignItems="center"
          marginRight="auto"
          spacing={1}
        >
          <AssetLocationFilterMenu
            label="From location"
            locationIds={fromLocationIds}
            customerIds={fromCustomerIds}
            companyTypes={fromCompanyTypes}
            onChange={handleChangeFromLocations}
          />
          <AssetLocationFilterMenu
            label="To location"
            locationIds={toLocationIds}
            customerIds={toCustomerIds}
            companyTypes={toCompanyTypes}
            onChange={handleChangeToLocations}
          />
          <SupportErrorBoundary message="An error occurred while loading shipment attention count.">
            <Suspense
              fallback={
                <NeedsAttentionFilterChipBase
                  isActive={props.searchParameters.needsAttention ?? false}
                  countStatus="loading"
                  onClick={handleNeedsAttentionToggle}
                />
              }
            >
              <NeedsAttentionFilterChip
                filter={shipmentFilter}
                isActive={props.searchParameters.needsAttention ?? false}
                onClick={handleNeedsAttentionToggle}
              />
            </Suspense>
          </SupportErrorBoundary>
          {props.shipmentType === 'outbound' && (
            <SupportErrorBoundary message="An error occurred while loading shipment quarantine count.">
              <Suspense
                fallback={
                  <QuarantinedFilterChipBase
                    isActive={props.searchParameters.quarantined ?? false}
                    countStatus="loading"
                    onClick={handleQuarantinedToggle}
                  />
                }
              >
                <QuarantinedFilterChip
                  filter={shipmentFilter}
                  isActive={props.searchParameters.quarantined ?? false}
                  onClick={handleQuarantinedToggle}
                />
              </Suspense>
            </SupportErrorBoundary>
          )}
        </Stack>
        <TextField
          inputRef={searchContainer}
          size="small"
          label="Search shipments"
          defaultValue={props.searchParameters.query}
          inputProps={{ maxLength: SEARCH_TERM_MAXIMUM_CHARACTERS }}
          onChange={handleChangeQuery}
          sx={{ width: { xs: '100%', sm: '20rem' } }}
          InputProps={{
            endAdornment: props.searchParameters.query && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => {
                    if (searchContainer.current) {
                      searchContainer.current.value = '';
                    }
                    updateQuery('');
                  }}
                >
                  <ClearRounded fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </TableControlContainer>

      {/* Shipment Table */}
      <SupportErrorBoundary message="An error occurred while loading shipments.">
        <Suspense fallback={<ShipmentTableSkeleton columns={7} />}>
          <ShipmentTable
            onPageChange={onPageChange}
            onShipShipment={handleShipShipment}
            sort={props.searchParameters.sort}
            direction={props.searchParameters.direction}
            onSort={handleSort}
            filter={shipmentFilter}
          />
        </Suspense>
      </SupportErrorBoundary>
    </Stack>
  );
}
